import { Button, Modal } from '@orbita-ui/core';
import { useCallback, useContext, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Card } from 'shared/components';
import { PageTitle } from 'shared/components/Fonts';
import { useNavigate } from 'react-router-dom';
import { Usuario } from 'user/models/Usuario';
import { useUserService } from 'user/services/useUserService';
import { CREATION_USERS_PATH, DETAIL_USERS_PATH, EDIT_USERS_PATH } from 'user/routes';
import { BaseModal, useModal } from 'shared/modal';
import { useImpersonationGuard } from 'auth/hooks/useImpersonationGuard';
import { FilterUsersForm } from 'user/components/FilterUsersForm/FilterUsersForm';
import { UsersTableWithPagination } from 'user/components/UsersTableWithPagination/UsersTableWithPagination';
import { UserListContext } from 'user/context/UserListContext';

export function UsersListPage() {
  const { searchParams, reloadToken, reloadUsersAndFilters } =
    useContext(UserListContext);

  const navigate = useNavigate();
  const userService = useUserService();

  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { showModal, closeModal, openModal } = useModal(false);
  const { guardWriteAction } = useImpersonationGuard();

  const handleDelete = useCallback(async () => {
    if (!selectedUser || !guardWriteAction()) {
      return;
    }
    try {
      await userService.deleteUser(selectedUser);
      closeModal();
      reloadUsersAndFilters();
    } catch (e) {
      setErrorMessage('Hubo un error al eliminar el usuario');
    }
  }, [selectedUser, reloadUsersAndFilters, closeModal, userService, guardWriteAction]);

  return (
    <>
      <Modal show={showModal} onClose={closeModal}>
        <BaseModal
          state={errorMessage ? 'error' : undefined}
          title={errorMessage ? 'Error' : 'Eliminar Usuario'}
          body={<p>{errorMessage ?? '¿Desea eliminar al usuario?'}</p>}
          footer={
            <>
              <Button
                className="bg-secondary"
                color="inverted"
                variant="ghost"
                onClick={closeModal}
              >
                Cancelar
              </Button>
              {!errorMessage && (
                <Button color="destructive" onClick={handleDelete}>
                  Eliminar
                </Button>
              )}
            </>
          }
        />
      </Modal>

      <Container fluid>
        <Row className="mb-3 d-flex justify-content-between align-items-center">
          <Col>
            <PageTitle>Usuarios</PageTitle>
          </Col>
          <Col className="text-end">
            <Button
              color="primary"
              icon="AddIcon"
              type="button"
              onClick={() => navigate(CREATION_USERS_PATH)}
            >
              Crear Usuario
            </Button>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Title>Filtros</Card.Title>
              <Card.Body>
                <FilterUsersForm />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <Card>
              <Card.Title>Usuarios</Card.Title>
              <Card.Body>
                <UsersTableWithPagination
                  searchParams={searchParams}
                  reloadToken={reloadToken}
                  onInfo={(user) =>
                    navigate(DETAIL_USERS_PATH.replace(':userId', `${user.id}`))
                  }
                  onEdit={(user) =>
                    navigate(EDIT_USERS_PATH.replace(':userId', `${user.id}`))
                  }
                  onDelete={(user) =>
                    guardWriteAction(() => {
                      setSelectedUser(user);
                      openModal();
                    })
                  }
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
